interface SuccessViewProps {
  name: string;
  onResend: () => void;
  /** False when the embed does not text the visitor, so there is no SMS step. */
  smsSent?: boolean;
}

function CheckBadge() {
  return (
    <div class="w-10 h-10 shrink-0 rounded-full bg-[#ecfdf5] flex items-center justify-center">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M5 10.5L8.5 14L15 7" stroke="#047857" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </div>
  );
}

function NumberBadge({ number, variant }: { number: number; variant: "active" | "upcoming" }) {
  const classes =
    variant === "active"
      ? "border-[#047857] text-[#047857]"
      : "border-[#d6d3d1] text-theme-fg";

  return (
    <div class={`w-8 h-8 shrink-0 rounded-full border-2 flex items-center justify-center text-sm font-bold ${classes}`}>
      {number}
    </div>
  );
}

function Separator() {
  return <div class="w-px h-3 bg-theme-border" />;
}

export function SuccessView({ name, onResend, smsSent = true }: SuccessViewProps) {
  const heading = name ? `Thanks, ${name}!` : "Thanks!";

  if (!smsSent) {
    return (
      <div class="font-theme animate-[fv-viewIn_0.45s_cubic-bezier(0.2,0.7,0.2,1)_both]">
        <div class="px-9 py-6 flex gap-4 items-center">
          <CheckBadge />
          <h2 class="m-0 min-w-0 [overflow-wrap:anywhere] text-[24px] font-semibold leading-8 text-theme-fg">
            {heading}
            <br />
            We'll be in touch shortly.
          </h2>
        </div>
      </div>
    );
  }

  return (
    <div class="font-theme animate-[fv-viewIn_0.45s_cubic-bezier(0.2,0.7,0.2,1)_both]">
      {/* Header */}
      <div class="px-9 py-6">
        <h2 class="m-0 text-[24px] font-semibold leading-8 text-theme-fg">
          {heading}
          <br />
          Two more steps for your free quote.
        </h2>
      </div>

      {/* Body — timeline */}
      <div class="px-9 py-6 border-t border-theme-border">
        <div class="flex flex-col">
          {/* Step 1 */}
          <div class="flex gap-4 items-center">
            <div class="w-10 shrink-0 flex justify-center">
              <CheckBadge />
            </div>
            <p class="m-0 text-lg font-semibold leading-7 text-theme-fg">
              We've received your contact details
            </p>
          </div>

          {/* Separator 1-2 */}
          <div class="w-10 shrink-0 flex justify-center py-1">
            <Separator />
          </div>

          {/* Step 2 */}
          <div class="flex gap-4 items-start">
            <div class="w-10 shrink-0 flex justify-center" style={{ paddingTop: "4px" }}>
              <NumberBadge number={2} variant="active" />
            </div>
            <div class="flex gap-7 items-start flex-1 min-w-0">
              <div class="flex-1 flex flex-col gap-1">
                <p class="m-0 text-lg font-semibold leading-7 text-theme-fg">Open the link in your SMS</p>
                <p class="m-0 text-sm font-normal leading-5 text-theme-muted-fg">
                  It arrives in under a minute. Tap it to open a short form on your phone.
                </p>
                <div class="flex items-center">
                  <p class="m-0 text-sm font-normal leading-5 text-theme-muted-fg">Didn't get the SMS?</p>
                  <a
                    class="inline-flex items-center h-9 px-4 text-sm font-medium text-theme-fg underline cursor-pointer hover:no-underline"
                    onClick={(e) => { e.preventDefault(); onResend(); }}
                    href="#"
                  >
                    Resend
                  </a>
                </div>
              </div>
              <div class="shrink-0 w-[102px] h-[102px] bg-[#f5f5f4] rounded-md flex items-center justify-center overflow-hidden p-4">
                <svg width="48" height="76" viewBox="0 0 48 76" fill="none">
                  <rect x="0.5" y="0.5" width="47" height="75" rx="8.5" fill="#fff" stroke="#0f172a" stroke-width="1" />
                  <rect x="18" y="4" width="12" height="2" rx="1" fill="#e2e8f0" />
                  <rect x="4" y="10" width="40" height="56" rx="2" fill="#f8fafc" />
                  <rect x="8" y="16" width="20" height="3" rx="1.5" fill="#e2e8f0" />
                  <rect x="8" y="22" width="28" height="3" rx="1.5" fill="#f1f5f9" />
                  <rect x="8" y="28" width="14" height="3" rx="1.5" fill="#f1f5f9" />
                  <rect x="16" y="70" width="16" height="2" rx="1" fill="#e2e8f0" />
                </svg>
              </div>
            </div>
          </div>

          {/* Separator 2-3 */}
          <div class="w-10 shrink-0 flex justify-center py-1">
            <Separator />
          </div>

          {/* Step 3 */}
          <div class="flex gap-4 items-start">
            <div class="w-10 shrink-0 flex justify-center" style={{ paddingTop: "4px" }}>
              <NumberBadge number={3} variant="upcoming" />
            </div>
            <div class="flex flex-col flex-1 min-w-0">
              <p class="m-0 text-lg font-semibold leading-7 text-theme-fg">Film your space, get your quote</p>
              <p class="m-0 text-sm font-normal leading-5 text-theme-muted-fg">
                A short video and a few photos is all we need. We'll send your personalized quote — no site visit required.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
